import {
  IconDefinition,
  faCheck,
  faExclamationTriangle,
  faInfoCircle,
  faMoneyBill,
  faStar,
  faWarning,
} from '@fortawesome/free-solid-svg-icons'
import React from 'react'
import { twMerge } from 'tailwind-merge'
import { Icon } from '../Icon/Icon'

export type NoticeTextProps = {
  type: 'warn' | 'error' | 'info' | 'success' | 'premium-feature' | 'cost-money'
  text?: string
  hideIcon?: boolean
  icon?: IconDefinition
  iconClasses?: string
  fullWidth?: boolean
  className?: string
  iconPosition?: 'left' | 'right'
  children?: React.ReactNode
  /** These classes are applied to the container surrounding both the text and children */
  contentContainerClasses?: string
  /** These classes are applied to the container surrounding the text */
  textClasses?: string
  /** These classes are applied to the container surrounding the children */
  childrenClasses?: string
}

export const NoticeText = (props: NoticeTextProps) => {
  const iconPosition = props.iconPosition || 'left'
  const getTextClasses = () => {
    if (props.type === 'warn') return 'text-orange-700'
    if (props.type === 'info') return 'text-blue-700'
    if (props.type === 'error') return 'text-red-700'
    if (props.type === 'success') return 'text-green-900'
    if (props.type === 'premium-feature') return 'text-amber-600'
    if (props.type === 'cost-money') return 'text-orange-900'
    return 'text-base-700'
  }
  const getBgClasses = () => {
    if (props.type === 'warn') return ' bg-yellow-100'
    if (props.type === 'info') return ' bg-blue-100'
    if (props.type === 'error') return ' bg-red-100'
    if (props.type === 'success') return 'bg-green-100'
    if (props.type === 'premium-feature')
      return 'bg-yellow-50 border rounded-md border-amber-200'
    if (props.type === 'cost-money') return 'bg-orange-100'
    return ' bg-base-100'
  }
  const getIconClasses = () => {
    if (props.type === 'warn') return 'text-orange-700'
    if (props.type === 'error') return 'text-red-700'
    if (props.type === 'info') return 'text-blue-700'
    if (props.type === 'success') return 'text-green-700'
    if (props.type === 'premium-feature') return 'text-amber-400'
    if (props.type === 'cost-money') return 'text-orange-700'
  }

  const getIcon = () => {
    if (props.hideIcon) return undefined
    if (props.icon) return props.icon
    if (props.type === 'warn') return faWarning
    if (props.type === 'error') return faExclamationTriangle
    if (props.type === 'success') return faCheck
    if (props.type === 'premium-feature') return faStar
    if (props.type === 'cost-money') return faMoneyBill
    return faInfoCircle
  }

  return (
    <div
      className={twMerge(
        `rounded-lg p-4 flex sm:flex-row items-center gap-4`,
        props.fullWidth ? 'w-full grow' : 'max-w-2xl',
        getBgClasses(),
        props.className,
      )}>
      <Icon
        className={twMerge(
          `${getIconClasses()} h-4! w-4! shrink`,
          iconPosition === 'left' ? '' : 'order-2',
          props.iconClasses,
        )}
        icon={getIcon()}
      />

      <div
        className={twMerge(
          'flex flex-wrap items-center justify-center grow gap-2',
          props.contentContainerClasses,
        )}>
        {props.text && (
          <p
            className={twMerge(
              `${getTextClasses()} sm:text-center grow text-sm`,
              props.textClasses,
            )}>
            {props.text}
          </p>
        )}

        {props.children && (
          <div
            className={twMerge(
              'justify-self-end sm:shrink min-w-fit',
              props.childrenClasses,
            )}>
            {props.children}
          </div>
        )}
      </div>
    </div>
  )
}
